import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import { pollUserLicenseActive } from '../../utils/pollUserLicenseActive';

const STATUS_COPY = {
  success: {
    title: 'Pagamento aprovado',
    icon: 'CheckCircle2',
    tone: 'text-success',
    message:
      'Seu pagamento foi aprovado. A licença será liberada em instantes assim que recebermos a confirmação do Mercado Pago.',
    cta: 'Ver minha licença',
    ctaHref: '/my-license',
  },
  pending: {
    title: 'Pagamento pendente',
    icon: 'Clock',
    tone: 'text-yellow-600',
    message:
      'Seu pagamento ainda está em processamento. Para PIX, a licença será liberada após a compensação.',
    cta: 'Ver histórico de compras',
    ctaHref: '/purchases',
  },
  failure: {
    title: 'Pagamento não concluído',
    icon: 'XCircle',
    tone: 'text-destructive',
    message:
      'O pagamento não foi concluído no Mercado Pago. Você pode tentar novamente escolhendo outra forma de pagamento.',
    cta: 'Tentar novamente',
    ctaHref: '/license-store',
  },
};

const resolveStatusFromPath = (pathname) => {
  if (pathname.endsWith('/success')) return 'success';
  if (pathname.endsWith('/pending')) return 'pending';
  if (pathname.endsWith('/failure')) return 'failure';
  return 'pending';
};

const PaymentReturnPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const status = useMemo(
    () => resolveStatusFromPath(location.pathname),
    [location.pathname]
  );

  const copy = STATUS_COPY[status] ?? STATUS_COPY.pending;
  const paymentId = searchParams.get('payment_id');
  const preferenceId = searchParams.get('preference_id');

  const shouldPollLicense = status === 'success' || status === 'pending';
  const [licensePollState, setLicensePollState] = useState(() =>
    shouldPollLicense ? 'polling' : 'idle'
  );

  useEffect(() => {
    if (!shouldPollLicense) {
      setLicensePollState('idle');
      return undefined;
    }

    const controller = new AbortController();
    setLicensePollState('polling');

    pollUserLicenseActive({ signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return;
        setLicensePollState(result.ok ? 'active' : 'waiting');
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLicensePollState('waiting');
        }
      });

    return () => controller.abort();
  }, [shouldPollLicense, status]);

  const licenseStatusLine = useMemo(() => {
    if (!shouldPollLicense) return null;
    if (licensePollState === 'polling') {
      return 'Verificando liberação da licença na sua conta…';
    }
    if (licensePollState === 'active') {
      return 'Licença ativa detectada. Você já pode usar os recursos do plano.';
    }
    return 'Ainda não vimos a licença ativa. Atualize em alguns minutos ou acesse Minha licença.';
  }, [licensePollState, shouldPollLicense]);

  return (
    <>
      <Helmet>
        <title>{copy.title} | Neo Modelos</title>
      </Helmet>

      <div className={`${PAGE_CONTENT_CLASS} max-w-2xl`}>
        <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-elevation-sm text-center space-y-6">
          <Icon name={copy.icon} size={56} className={`mx-auto ${copy.tone}`} />
          <div className="space-y-2">
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
              {copy.title}
            </h1>
            <p className="text-sm text-muted-foreground">{copy.message}</p>
            {licenseStatusLine ? (
              <p
                className={`text-sm mt-3 flex items-center justify-center gap-2 ${
                  licensePollState === 'active'
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-muted-foreground'
                }`}
              >
                {licensePollState === 'polling' ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                ) : (
                  <Icon
                    name={licensePollState === 'active' ? 'CheckCircle2' : 'Info'}
                    size={16}
                    className="shrink-0"
                  />
                )}
                {licenseStatusLine}
              </p>
            ) : null}
          </div>

          {(paymentId || preferenceId) && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-left text-xs text-muted-foreground space-y-1 break-all">
              {paymentId && <p>ID do pagamento: {paymentId}</p>}
              {preferenceId && <p>ID da preferência: {preferenceId}</p>}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button asChild className="w-full sm:w-auto">
              <Link to={copy.ctaHref}>{copy.cta}</Link>
            </Button>
            <Button variant="outline" asChild className="w-full sm:w-auto">
              <Link to="/license-store">Voltar à loja de licenças</Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentReturnPage;
